import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"

import { cn } from "@/lib/utils"
import { getResponsiveImageUrl, extractWidthFromClassName } from "@/utils/supabaseImageUrl"

// Context para passar o tamanho do Avatar para o AvatarImage
const AvatarSizeContext = React.createContext<number | undefined>(undefined);

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => {
  // Extrair width da className para passar ao contexto
  const width = extractWidthFromClassName(className);
  
  return (
    <AvatarSizeContext.Provider value={width}>
      <AvatarPrimitive.Root
        ref={ref}
        className={cn(
          "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
          className
        )}
        {...props}
      />
    </AvatarSizeContext.Provider>
  );
})
Avatar.displayName = AvatarPrimitive.Root.displayName

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image> & {
    width?: number; // Prop opcional para sobrescrever o tamanho do contexto
  }
>(({ className, src, width: propWidth, ...props }, ref) => {
  const imageRef = React.useRef<HTMLImageElement>(null);
  const [actualWidth, setActualWidth] = React.useState<number | undefined>();
  
  // Usar width do prop ou do contexto, ou usar um padrão razoável (80px)
  const contextWidth = React.useContext(AvatarSizeContext);
  const baseWidth = propWidth ?? contextWidth ?? 80;
  
  // Combinar refs
  const combinedRef = React.useCallback((node: HTMLImageElement | null) => {
    imageRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);

  // Calcular tamanho real do elemento para responsividade
  React.useEffect(() => {
    if (!imageRef.current) return;
    
    const updateSize = () => {
      if (imageRef.current) {
        const rect = imageRef.current.getBoundingClientRect();
        if (rect && rect.width > 0) {
          const newWidth = Math.ceil(rect.width);
          // Só atualizar se mudou significativamente (evitar loops)
          setActualWidth(prev => {
            if (!prev || Math.abs(newWidth - prev) > 1) {
              return newWidth;
            }
            return prev;
          });
        }
      }
    };
    
    // Atualizar após um pequeno delay para garantir que o elemento está renderizado
    const timeoutId = setTimeout(updateSize, 50);
    
    // Observar mudanças de tamanho (apenas se ResizeObserver estiver disponível)
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && imageRef.current) {
      resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(imageRef.current);
    }
    
    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []); // Executar apenas uma vez na montagem

  // Gerar URLs responsivas
  const imageUrls = React.useMemo(() => {
    if (!src) return { src: undefined, srcset: undefined };
    
    // Usar tamanho real se disponível, senão usar baseWidth
    // Multiplicar por devicePixelRatio para telas retina
    const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const widthToUse = actualWidth || baseWidth;
    
    const responsive = getResponsiveImageUrl(src, widthToUse, devicePixelRatio);
    
    // Garantir que sempre retornamos uma src válida
    return {
      src: responsive.src || src,
      srcset: responsive.srcset,
    };
  }, [src, baseWidth, actualWidth]);

  return (
    <AvatarPrimitive.Image
      ref={combinedRef}
      className={cn("aspect-square h-full w-full", className)}
      src={imageUrls.src}
      srcSet={imageUrls.srcset}
      {...props}
    />
  );
})
AvatarImage.displayName = AvatarPrimitive.Image.displayName

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className
    )}
    {...props}
  />
))
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

export { Avatar, AvatarImage, AvatarFallback }
